import { useMemo, useState } from 'react';
import {
  formatRangeWindowNoDataText,
  type DashboardRange,
  useDashboardDateRange,
} from '@/features/dashboard/hooks';
import {
  resolveExecutionRunsToolbarDates,
  resolveExecutionRunsToolbarQuickFilter,
} from '@/features/uploads/pages/executionRunsPageToolbar';
import {
  buildDateRangeFilters,
  buildInFilter,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import type { ApiFilter } from '@/ui/composites/grid/gridUtils';

type UseUploadsTableToolbarStateParams<Status extends string> = {
  dateField: string;
  noRowsBaseText: string;
  initialStatuses?: Status[];
};

type UseUploadsTableToolbarStateResult<Status extends string> = {
  search: string;
  setSearch: (value: string) => void;
  selectedStatuses: Status[];
  setSelectedStatuses: (value: Status[]) => void;
  selectedTags: string[];
  setSelectedTags: (value: string[]) => void;
  fixedFilters: ApiFilter[];
  noRowsText: string;
  dateQuickFilter: '7d' | '14d' | '30d' | 'latest';
  onDateQuickFilterChange: (value: DashboardRange) => void;
  fromDate: ReturnType<typeof resolveExecutionRunsToolbarDates>['fromDate'];
  toDate: ReturnType<typeof resolveExecutionRunsToolbarDates>['toDate'];
  onFromDateChange: (value: string | null) => void;
  onToDateChange: (value: string | null) => void;
};

export const useUploadsTableToolbarState = <Status extends string>({
  dateField,
  noRowsBaseText,
  initialStatuses = [],
}: UseUploadsTableToolbarStateParams<Status>): UseUploadsTableToolbarStateResult<Status> => {
  const [search, setSearch] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<Status[]>(initialStatuses);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [range, setRange] = useState<DashboardRange>('7d');
  const [startDay, setStartDay] = useState<string | null>(null);
  const [endDay, setEndDay] = useState<string | null>(null);
  const dashboardRange = useDashboardDateRange(range);
  const hasCustomDates = Boolean(startDay ?? endDay);
  const dateRangeFilters = useMemo(
    () =>
      buildDateRangeFilters(dateField, dashboardRange.rangeStartIso, dashboardRange.rangeEndIso),
    [dashboardRange.rangeEndIso, dashboardRange.rangeStartIso, dateField],
  );
  const selectedDayFilters = useMemo(() => {
    if (!startDay && !endDay) return [];
    const start = startDay ? `${startDay}T00:00:00.000Z` : null;
    const end = endDay ? `${endDay}T23:59:59.999Z` : null;
    return buildDateRangeFilters(dateField, start, end);
  }, [dateField, endDay, startDay]);
  const fixedFilters = useMemo(
    () =>
      mergeFilters(
        selectedDayFilters,
        dateRangeFilters,
        buildInFilter('status', selectedStatuses),
        buildInFilter('tags', selectedTags),
      ),
    [dateRangeFilters, selectedDayFilters, selectedStatuses, selectedTags],
  );
  const noRowsText = formatRangeWindowNoDataText(noRowsBaseText, range, hasCustomDates);
  const toolbarDates = resolveExecutionRunsToolbarDates({ hasCustomDates, startDay, endDay });
  const dateQuickFilter = resolveExecutionRunsToolbarQuickFilter(range);

  return {
    search,
    setSearch,
    selectedStatuses,
    setSelectedStatuses,
    selectedTags,
    setSelectedTags,
    fixedFilters,
    noRowsText,
    dateQuickFilter,
    onDateQuickFilterChange: (value) => {
      setRange(value);
      setStartDay(null);
      setEndDay(null);
    },
    fromDate: toolbarDates.fromDate,
    toDate: toolbarDates.toDate,
    onFromDateChange: setStartDay,
    onToDateChange: setEndDay,
  };
};
